import { uploadFileApi, useFileSignedUrl } from "@/api/services/global/file";
import { useFormField } from "@/components/elements/FormField/FormField";
import { Button } from "@/components/elements/Button";
import { FILE_UPLOAD_FOR, MIME_TYPES } from "@/constants/file";
import { useToast } from "@/hooks/useToast";
import { divider, text } from "@/theme/colors";
import { convertMbToByte } from "@/utils/file";
import { Box, Stack, Typography } from "@mui/material";
import { ChangeEvent, useRef, useState } from "react";

const MAX_LOGO_SIZE = convertMbToByte(10);
const ACCEPT_MIME_TYPES = [...MIME_TYPES.IMAGE, ...MIME_TYPES.SVG];

// Some browsers/OS report an empty or non-standard MIME type for SVG, so fall back to the extension.
const isAcceptedFile = (file: File) => ACCEPT_MIME_TYPES.includes(file.type);

export const ProductLogoUpload = () => {
  const { field } = useFormField();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const filePath = (field?.value as string) || "";
  const { data: signed } = useFileSignedUrl(filePath || undefined);
  const previewUrl = signed?.data?.signedUrl;

  const handleSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!isAcceptedFile(file)) {
      toast.error("画像ファイル（PNG / JPEG / GIF / SVG）を選択してください。");
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      toast.error("ファイルサイズは10MB以下にしてください。");
      return;
    }

    try {
      setUploading(true);
      const rs = await uploadFileApi({ uploadFor: FILE_UPLOAD_FOR.PRODUCT_LOGO, file });
      field?.onChange(rs.data.filePath);
    } catch {
      toast.error("アップロードに失敗しました。");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => field?.onChange("");

  return (
    <Stack direction="row" gap={2} alignItems="center">
      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: "8px",
          border: `1px solid ${divider.middle}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {previewUrl ? (
          <img src={previewUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        ) : (
          <Typography variant="cap12" color={text.secondary}>
            未設定
          </Typography>
        )}
      </Box>

      <Stack gap={1}>
        <Stack direction="row" gap={1}>
          <Button size="sm" variant="ghost" disabled={uploading} onClick={() => inputRef.current?.click()}>
            画像をアップロード
          </Button>
          {filePath && (
            <Button size="sm" variant="ghost" onClick={handleRemove}>
              削除
            </Button>
          )}
        </Stack>
        <Typography variant="cap12" color={text.secondary}>
          PNG / JPEG / GIF / SVG、最大10MB
        </Typography>
      </Stack>

      <input ref={inputRef} type="file" accept={ACCEPT_MIME_TYPES.join(",")} hidden onChange={handleSelect} />
    </Stack>
  );
};
